import styled from "styled-components";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Image } from "../../components/Image";
import { Paragraph } from "../../components/Paragraph";
import { StatusBadge, StatusIntent } from "../../components/StatusBadge";
import success from "../../assets/card-success-icon.svg";
import inProgress from "../../assets/card-indeterminate-icon-gray.svg";
import inReview from "../../assets/card-in-review-icon.svg";

/**
 * Layout pieces for the ID & Liveness check view: each sub-result sits in its
 * own white card with a title row led by the Portal's usual status icon.
 * Kept local to this view so the other check views are untouched.
 */
export const Panel = styled(Box)`
  ${({ theme: { colors, radii, shadows, space } }) => `
    background: ${colors.white};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[1]};
    box-shadow: ${shadows.sm};
    padding: ${space[3]};
    margin-bottom: ${space[3]};
  `}
`;

const PanelTitle = styled(H3)`
  ${({ theme: { fontSizes, fontWeights } }) => `
    font-size: ${fontSizes[2]};
    font-weight: ${fontWeights.semibold};
    margin: 0;
  `}
`;

/** Small uppercase section label. gray.60 keeps it readable at 12px. */
export const SubHeading = styled(Paragraph)`
  ${({ theme: { colors, fontSizes, fontWeights, letterSpacings, space } }) => `
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    letter-spacing: ${letterSpacings.wide};
    text-transform: uppercase;
    color: ${colors.gray[60]};
    margin: 0 0 ${space[1]} 0;
  `}
`;

export const Divider = styled.hr`
  ${({ theme: { colors, space } }) => `
    border: none;
    border-top: 1px solid ${colors.gray[100]};
    margin: ${space[3]} 0;
  `}
`;

/**
 * Label and value pairs as ruled rows, two columns where there is room and
 * one where there is not. Easier to scan than a grid of label-over-value
 * tiles, and the 14px gray.60 labels stay sharp. Nothing sets a global
 * font-family in this app, so the grid states it or the text falls back to
 * the browser's default serif.
 */
const DetailGrid = styled.div`
  ${({ theme: { fontFamily, space } }) => `
    font-family: ${fontFamily};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    column-gap: ${space[4]};
  `}
`;

/** Fills each column top to bottom before starting the next. One column
 * where there is no room for more. */
const DetailColumns = styled.div<{ $count: number }>`
  ${({ theme: { fontFamily, space }, $count }) => `
    font-family: ${fontFamily};
    columns: ${$count} 220px;
    column-gap: ${space[4]};
  `}
`;

const DetailRow = styled.div`
  ${({ theme: { colors, fontSizes, space } }) => `
    break-inside: avoid;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${space[2]};
    padding: ${space[1]} 0;
    border-bottom: 1px solid ${colors.divider};
    font-size: ${fontSizes[1]};
    min-width: 0;
  `}
`;

const DetailLabel = styled.span`
  ${({ theme: { colors } }) => `
    color: ${colors.gray[60]};
    flex: 0 0 auto;
    max-width: 50%;
  `}
`;

const DetailValue = styled.span`
  ${({ theme: { colors, fontWeights } }) => `
    color: ${colors.gray[80]};
    font-weight: ${fontWeights.semibold};
    text-align: right;
    overflow-wrap: anywhere;
    min-width: 0;
  `}
`;

const isEmpty = (value: any) =>
  value === null || value === undefined || value === "";

/** Rows fill left to right by default, or down a fixed number of columns. */
export const DetailList = ({
  items,
  columns,
}: {
  items: { label: string; value: any }[];
  columns?: number;
}) => {
  const rows = items.map((item, i) => (
    <DetailRow key={i}>
      <DetailLabel>{item.label}</DetailLabel>
      <DetailValue>{isEmpty(item.value) ? "-" : item.value}</DetailValue>
    </DetailRow>
  ));
  return columns ? (
    <DetailColumns $count={columns}>{rows}</DetailColumns>
  ) : (
    <DetailGrid>{rows}</DetailGrid>
  );
};

/** Map a sub-check or provider status string to a badge intent. */
export const checkStatusIntent = (status?: string | null): StatusIntent => {
  switch ((status ?? "").toLowerCase()) {
    case "passed":
    case "pass":
    case "accepted":
    case "manually accepted":
      return "success";
    case "review":
    case "refer":
      return "warning";
    case "failed":
    case "fail":
    case "rejected":
    case "manually rejected":
      return "danger";
    default:
      return "neutral";
  }
};

/** The Portal's check status icons, keyed off a sub-check status string. */
export const StatusIcon = ({
  status,
  size = "20px",
}: {
  status?: string | null;
  size?: string;
}) => {
  const intent = checkStatusIntent(status);
  const src =
    intent === "success"
      ? success
      : intent === "neutral"
      ? inProgress
      : inReview;
  return (
    <Image
      src={src}
      alt={status ? `${status} icon` : "status icon"}
      height={size}
      width={size}
      flexShrink={0}
    />
  );
};

export const PanelHeader = ({
  title,
  subtitle,
  status,
  actions,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: string | null;
  actions?: React.ReactNode;
}) => (
  <Flex justifyContent="space-between" alignItems="flex-start" gap="16" mb={2}>
    <Flex alignItems="center" gap="8" minWidth={0}>
      {status && <StatusIcon status={status} />}
      <Box minWidth={0}>
        <PanelTitle>{title}</PanelTitle>
        {subtitle && (
          <Paragraph m="0px" fontSize={1} color="gray.60">
            {subtitle}
          </Paragraph>
        )}
      </Box>
    </Flex>
    {(status || actions) && (
      <Flex gap="8" alignItems="center" flexShrink={0}>
        {status && (
          <StatusBadge intent={checkStatusIntent(status)}>{status}</StatusBadge>
        )}
        {actions}
      </Flex>
    )}
  </Flex>
);
